// components/clubs/CreateClubForm.tsx
// -----------------------------------------------------------------------------
// Create/Edit club form matching mobile create-edit-club structure.
// Supports both standalone SidePanel mode and content-only mode for SidePanelStack.
// -----------------------------------------------------------------------------

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Icon } from "@/components/ui/Icon";
import { Switch } from "@/components/ui/Switch";
import { useCreateClubMutation, useUpdateClubMutation, type Club } from "@/api/clubsApi";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/components/ui/cn";
import { LocationPickerModal, LocationView } from "@/components/location";
import { uploadImage } from "@/utils/uploadUtils";
import type { ImageUploadResult } from "@/utils/uploadUtils";
import type { GeocodingFeature } from "@tinyclub/shared/models";

interface CreateClubFormProps {
  creatorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional club to edit - if provided, form operates in edit mode */
  club?: Club | null;
}

/**
 * CreateClubForm component for creating or editing clubs.
 * Matches mobile create-edit-club structure and styling.
 */
export function CreateClubForm({
  creatorId,
  open,
  onOpenChange,
  club,
}: CreateClubFormProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditMode = Boolean(club?.id);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploadResult, setImageUploadResult] = useState<ImageUploadResult | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Club settings - matching mobile defaults
  const [anyoneCanPost, setAnyoneCanPost] = useState(true);
  const [anyoneCanCreateHangs, setAnyoneCanCreateHangs] = useState(true);
  const [anyoneCanJoin, setAnyoneCanJoin] = useState(true);
  const [showMemberCount, setShowMemberCount] = useState(true);
  const [directoryEnabled, setDirectoryEnabled] = useState(true);

  // Location state
  const [locationObject, setLocationObject] = useState<GeocodingFeature | null>(null);
  const [locationName, setLocationName] = useState("");
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  const [createClub, { isLoading: isCreating }] = useCreateClubMutation();
  const [updateClub, { isLoading: isUpdating }] = useUpdateClubMutation();
  const isLoading = isCreating || isUpdating;

  // Pre-populate form when editing
  useEffect(() => {
    if (club && open) {
      setName(club.name || "");
      setDescription(club.description || "");
      setAnyoneCanPost(club.post_permission === "everyone");
      setAnyoneCanCreateHangs(club.hang_permission === "everyone");
      setAnyoneCanJoin(!club.private);
      setShowMemberCount(club.show_member_count !== false);
      setDirectoryEnabled(club.directory_enabled !== false);
      setLocationObject(club.location_object as GeocodingFeature | null);
      setLocationName(club.location_name || "");
      // Set image preview from existing club image
      if (club.primary_image_medium || club.primary_image_small) {
        setImagePreview(club.primary_image_medium || club.primary_image_small || null);
      }
    }
  }, [club, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    try {
      // Build image fields if an image was uploaded
      const imageFields = imageUploadResult
        ? {
            primary_image_small: imageUploadResult.imageSmall,
            primary_image_medium: imageUploadResult.imageMedium,
            primary_image_full: imageUploadResult.imageFull,
          }
        : {};

      if (isEditMode && club?.id) {
        // Update existing club
        const result = await updateClub({
          id: club.id,
          name: name.trim(),
          description: description.trim() || undefined,
          private: !anyoneCanJoin,
          post_permission: anyoneCanPost ? "everyone" : "managers",
          hang_permission: anyoneCanCreateHangs ? "everyone" : "managers",

          show_member_count: showMemberCount,
          directory_enabled: directoryEnabled,
          location_object: locationObject ?? null,
          location_name: locationName || null,
          ...imageFields,
        }).unwrap();

        if (result) {
          // Close dialog on success
          onOpenChange(false);
        }
      } else {
        // Create new club
        const result = await createClub({
          name: name.trim(),
          description: description.trim() || undefined,
          creatorId,
          private: !anyoneCanJoin,
          post_permission: anyoneCanPost ? "everyone" : "managers",
          hang_permission: anyoneCanCreateHangs ? "everyone" : "managers",

          show_member_count: showMemberCount,
          directory_enabled: directoryEnabled,
          location_object: locationObject ?? null,
          location_name: locationName || null,
          ...imageFields,
        }).unwrap();

        if (result?.id) {
          // Reset form and close dialog on success
          resetForm();
          onOpenChange(false);

          // Navigate to the new club
          navigate({ to: "/clubs/$identifier", params: { identifier: result.id } });
        }
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? "update" : "create"} club:`, error);
      showToast(`Failed to ${isEditMode ? "update" : "create"} club. Please try again.`, "error");
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setImagePreview(null);
    setImageUploadResult(null);
    setIsUploadingImage(false);
    setUploadProgress(0);
    setAnyoneCanPost(true);
    setAnyoneCanCreateHangs(true);
    setAnyoneCanJoin(true);
    setShowMemberCount(true);
    setDirectoryEnabled(true);
    setLocationObject(null);
    setLocationName("");
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImagePreview(URL.createObjectURL(file));
    setIsUploadingImage(true);
    setUploadProgress(0);

    try {
      const result = await uploadImage(file, (progress) => {
        setUploadProgress(progress);
      });
      setImageUploadResult(result);
    } catch (error) {
      console.error("Failed to upload club image:", error);
      showToast("Failed to upload image. Please try again.", "error");
      setImagePreview(null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onOpenChange(false);
    }
  };

  const canSubmit = name.trim().length > 0 && !isLoading && !isUploadingImage;

  return (
    <SidePanel open={open} onOpenChange={handleClose}>
      <SidePanelContent>
        <SidePanelHeader title={isEditMode ? "Edit Club" : "Create Club"} onClose={handleClose} />
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <SidePanelBody>
            <div className="space-y-6">
            {/* Club image section */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              {imagePreview ? (
                <div className="relative aspect-square w-full rounded-2xl overflow-hidden border border-border-tertiary">
                  <img
                    src={imagePreview}
                    alt="Club preview"
                    className={cn("w-full h-full object-cover", isUploadingImage && "opacity-50")}
                  />
                  {isUploadingImage && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-body-2-medium text-white bg-black/60 px-4 py-2 rounded-full">
                        {Math.round(uploadProgress * 100)}%
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleImageClick}
                    disabled={isUploadingImage}
                    className="absolute bottom-4 right-4 px-4 py-2 rounded-full bg-black/60 text-white text-body-2-medium hover:bg-black/70 transition-colors"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleImageClick}
                  className="w-full aspect-square rounded-2xl border border-border-tertiary bg-surface flex flex-col items-center justify-center hover:bg-surface-2 transition-colors"
                >
                  <div className="px-4 py-2 rounded-full bg-surface-2 text-body-2-medium text-text-secondary">
                    Add club image
                  </div>
                </button>
              )}
            </div>

            {/* Club name section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">Club name</span>
                <span className="text-body-2 text-text-tertiary">Required</span>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface">
                <Icon name="globe" size={16} className="text-text-primary mt-1 flex-shrink-0" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name your club"
                  className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-tertiary focus:outline-none"
                  disabled={isLoading}
                  autoFocus
                  maxLength={100}
                />
              </div>
            </div>

            {/* What's this club about? section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">What's this club about?</span>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface min-h-[120px]">
                <Icon name="comment" size={16} className="text-text-primary mt-1 flex-shrink-0" />
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description"
                  className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none min-h-[80px]"
                  disabled={isLoading}
                  maxLength={500}
                />
              </div>
            </div>

            {/* Location section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">Location</span>
              </div>
              <p className="text-body-2 text-text-secondary mb-3 px-2">
                Where is your club located? Share the general area or specific meeting spot.
              </p>
              <LocationView
                editable
                location={locationObject}
                locationName={locationName}
                onLocationClick={() => setLocationPickerOpen(true)}
                onClearLocation={() => {
                  setLocationObject(null);
                  setLocationName("");
                }}
              />
            </div>

            {/* Club settings section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">Club settings</span>
              </div>
              <div className="rounded-2xl bg-surface p-4 space-y-4">
                {/* Anyone can post */}
                <SettingsToggle
                  label="Anyone can post"
                  description="When enabled, anyone can post to the club. When disabled, only club hosts can post."
                  checked={anyoneCanPost}
                  onCheckedChange={setAnyoneCanPost}
                />

                <Divider />

                {/* Anyone can create hangs */}
                <SettingsToggle
                  label="Anyone can create hangs"
                  description="When enabled, anyone can create hangs for the club. When disabled, only club hosts can create hangs."
                  checked={anyoneCanCreateHangs}
                  onCheckedChange={setAnyoneCanCreateHangs}
                />

                <Divider />

                {/* Anyone can join */}
                <SettingsToggle
                  label="Anyone can join"
                  description="When enabled, anyone with the link can join without host approval."
                  checked={anyoneCanJoin}
                  onCheckedChange={setAnyoneCanJoin}
                />

                <Divider />

                {/* Show member count */}
                <SettingsToggle
                  label="Show member count"
                  description="When enabled, people can see the number of members in this club."
                  checked={showMemberCount}
                  onCheckedChange={setShowMemberCount}
                />

                <Divider />

                {/* Allow others to discover */}
                <SettingsToggle
                  label="Allow others to discover"
                  description="When enabled, your club may be recommended to others who are nearby or have shared interests."
                  checked={directoryEnabled}
                  onCheckedChange={setDirectoryEnabled}
                />
              </div>
            </div>
            </div>
          </SidePanelBody>

          {/* Footer with buttons */}
          <SidePanelFooter className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-3 rounded-full text-body-2-medium text-text-secondary hover:bg-surface-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={cn(
                "flex items-center justify-center gap-2 px-4 py-3 rounded-full text-body-2-medium transition-opacity",
                canSubmit
                  ? "bg-olive text-white hover:opacity-90"
                  : "bg-olive/50 text-white/70 cursor-not-allowed"
              )}
            >
              {isLoading
                ? isEditMode ? "Saving..." : "Creating..."
                : isEditMode ? "Save Changes" : "Create Club"
              }
            </button>
          </SidePanelFooter>
        </form>

        <LocationPickerModal
          open={locationPickerOpen}
          onOpenChange={setLocationPickerOpen}
          onLocationSelect={(location, name) => {
            setLocationObject(location);
            setLocationName(name || "");
            setLocationPickerOpen(false);
          }}
          initialLocation={locationObject}
          initialLocationName={locationName}
        />
      </SidePanelContent>
    </SidePanel>
  );
}

// Settings toggle row component
interface SettingsToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function SettingsToggle({ label, description, checked, onCheckedChange }: SettingsToggleProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 space-y-1">
        <span className="text-body-2-medium text-text-primary">{label}</span>
        <p className="text-body-2 text-text-secondary">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

// Simple divider component
function Divider() {
  return <div className="h-px bg-border-tertiary" />;
}

// ============================================================================
// EditClubFormContent - Content-only version for use inside SidePanelStack
// ============================================================================

interface EditClubFormContentProps {
  club: Club;
  onSave?: () => void;
}

/**
 * EditClubFormContent - The form content without the SidePanel wrapper.
 * Use this inside a SidePanelStack for push-over navigation.
 */
export function EditClubFormContent({ club, onSave }: EditClubFormContentProps) {
  const { showToast } = useToast();
  const [name, setName] = useState(club.name || "");
  const [description, setDescription] = useState(club.description || "");
  const [imagePreview, setImagePreview] = useState<string | null>(
    club.primary_image_medium || club.primary_image_small || null
  );
  const [imageUploadResult, setImageUploadResult] = useState<ImageUploadResult | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Club settings
  const [anyoneCanPost, setAnyoneCanPost] = useState(club.post_permission === "everyone");
  const [anyoneCanCreateHangs, setAnyoneCanCreateHangs] = useState(club.hang_permission === "everyone");
  const [anyoneCanJoin, setAnyoneCanJoin] = useState(!club.private);
  const [showMemberCount, setShowMemberCount] = useState(club.show_member_count !== false);
  const [directoryEnabled, setDirectoryEnabled] = useState(club.directory_enabled !== false);

  // Location state
  const [locationObject, setLocationObject] = useState<GeocodingFeature | null>(
    club.location_object as GeocodingFeature | null
  );
  const [locationName, setLocationName] = useState(club.location_name || "");
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  const [updateClub, { isLoading }] = useUpdateClubMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    try {
      const imageFields = imageUploadResult
        ? {
            primary_image_small: imageUploadResult.imageSmall,
            primary_image_medium: imageUploadResult.imageMedium,
            primary_image_full: imageUploadResult.imageFull,
          }
        : {};

      const result = await updateClub({
        id: club.id!,
        name: name.trim(),
        description: description.trim() || undefined,
        private: !anyoneCanJoin,
        post_permission: anyoneCanPost ? "everyone" : "managers",
        hang_permission: anyoneCanCreateHangs ? "everyone" : "managers",
        show_member_count: showMemberCount,
        directory_enabled: directoryEnabled,
        location_object: locationObject ?? null,
        location_name: locationName || null,
        ...imageFields,
      }).unwrap();

      if (result) {
        onSave?.();
      }
    } catch (error) {
      console.error("Failed to update club:", error);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImagePreview(URL.createObjectURL(file));
    setIsUploadingImage(true);
    setUploadProgress(0);

    try {
      const result = await uploadImage(file, (progress) => {
        setUploadProgress(progress);
      });
      setImageUploadResult(result);
    } catch (error) {
      console.error("Failed to upload club image:", error);
      showToast("Failed to upload image. Please try again.", "error");
      setImagePreview(club.primary_image_medium || club.primary_image_small || null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const canSubmit = name.trim().length > 0 && !isLoading && !isUploadingImage;

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <SidePanelBody>
          <div className="space-y-6">
            {/* Club image section */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              {imagePreview ? (
                <div className="relative aspect-square w-full rounded-2xl overflow-hidden border border-border-tertiary">
                  <img
                    src={imagePreview}
                    alt="Club preview"
                    className={cn("w-full h-full object-cover", isUploadingImage && "opacity-50")}
                  />
                  {isUploadingImage && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-body-2-medium text-white bg-black/60 px-4 py-2 rounded-full">
                        {Math.round(uploadProgress * 100)}%
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleImageClick}
                    disabled={isUploadingImage}
                    className="absolute bottom-4 right-4 px-4 py-2 rounded-full bg-black/60 text-white text-body-2-medium hover:bg-black/70 transition-colors"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleImageClick}
                  className="w-full aspect-square rounded-2xl border border-border-tertiary bg-surface flex flex-col items-center justify-center hover:bg-surface-2 transition-colors"
                >
                  <div className="px-4 py-2 rounded-full bg-surface-2 text-body-2-medium text-text-secondary">
                    Add club image
                  </div>
                </button>
              )}
            </div>

            {/* Club name section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">Club name</span>
                <span className="text-body-2 text-text-tertiary">Required</span>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface">
                <Icon name="globe" size={16} className="text-text-primary mt-1 flex-shrink-0" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name your club"
                  className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-tertiary focus:outline-none"
                  disabled={isLoading}
                  maxLength={100}
                />
              </div>
            </div>

            {/* What's this club about? section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">What's this club about?</span>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface min-h-[120px]">
                <Icon name="comment" size={16} className="text-text-primary mt-1 flex-shrink-0" />
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description"
                  className="flex-1 bg-transparent text-body-1 text-text-primary placeholder:text-text-tertiary resize-none focus:outline-none min-h-[80px]"
                  disabled={isLoading}
                  maxLength={500}
                />
              </div>
            </div>

            {/* Location section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">Location</span>
              </div>
              <p className="text-body-2 text-text-secondary mb-3 px-2">
                Where is your club located? Share the general area or specific meeting spot.
              </p>
              <LocationView
                editable
                location={locationObject}
                locationName={locationName}
                onLocationClick={() => setLocationPickerOpen(true)}
                onClearLocation={() => {
                  setLocationObject(null);
                  setLocationName("");
                }}
              />
            </div>

            {/* Club settings section */}
            <div>
              <div className="flex justify-between items-center mb-3 px-2">
                <span className="text-h6 text-text-primary">Club settings</span>
              </div>
              <div className="rounded-2xl bg-surface p-4 space-y-4">
                <SettingsToggle
                  label="Anyone can post"
                  description="When enabled, anyone can post to the club. When disabled, only club hosts can post."
                  checked={anyoneCanPost}
                  onCheckedChange={setAnyoneCanPost}
                />

                <Divider />

                <SettingsToggle
                  label="Anyone can create hangs"
                  description="When enabled, anyone can create hangs for the club. When disabled, only club hosts can create hangs."
                  checked={anyoneCanCreateHangs}
                  onCheckedChange={setAnyoneCanCreateHangs}
                />

                <Divider />

                <SettingsToggle
                  label="Anyone can join"
                  description="When enabled, anyone with the link can join without host approval."
                  checked={anyoneCanJoin}
                  onCheckedChange={setAnyoneCanJoin}
                />

                <Divider />

                <SettingsToggle
                  label="Show member count"
                  description="When enabled, people can see the number of members in this club."
                  checked={showMemberCount}
                  onCheckedChange={setShowMemberCount}
                />

                <Divider />

                <SettingsToggle
                  label="Allow others to discover"
                  description="When enabled, your club may be recommended to others who are nearby or have shared interests."
                  checked={directoryEnabled}
                  onCheckedChange={setDirectoryEnabled}
                />
              </div>
            </div>
          </div>
        </SidePanelBody>

        {/* Footer with save button */}
        <SidePanelFooter className="flex gap-3 justify-end">
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              "flex items-center justify-center gap-2 px-4 py-3 rounded-full text-body-2-medium transition-opacity",
              canSubmit
                ? "bg-olive text-white hover:opacity-90"
                : "bg-olive/50 text-white/70 cursor-not-allowed"
            )}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
        </SidePanelFooter>
      </form>

      <LocationPickerModal
        open={locationPickerOpen}
        onOpenChange={setLocationPickerOpen}
        onLocationSelect={(location, name) => {
          setLocationObject(location);
          setLocationName(name || "");
          setLocationPickerOpen(false);
        }}
        initialLocation={locationObject}
        initialLocationName={locationName}
      />
    </>
  );
}
